import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { projects, getProjectSlug } from '../data/projects';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SEO from '../components/SEO';
import StructuredData, { generateCollectionPageSchema } from '../components/StructuredData';
import './WorkPage.css';

gsap.registerPlugin(ScrollTrigger);

const WorkPage = () => {
    const navigate = useNavigate();
    const [hoveredProject, setHoveredProject] = useState(null);
    const [hoverTimestamp, setHoverTimestamp] = useState(0);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [previewScale, setPreviewScale] = useState(0.7);
    const [useSlideshow, setUseSlideshow] = useState(false);
    const [slideshowIndex, setSlideshowIndex] = useState(0);
    const projectRefs = useRef([]);
    const titleRefs = useRef([]);
    const hoverTimerRef = useRef(null);
    const slideshowIntervalRef = useRef(null);
    const lastTapRef = useRef({ id: null, time: 0 });
    const scrambleTimersRef = useRef({});

    const typewriterText = (element, originalText, onComplete) => {
        if (!element) return;
        
        const originalLength = originalText.length;
        let currentIndex = 0;
        
        const showCursor = () => {
            return '<span class="typewriter-cursor">|</span>';
        };
        
        const updateText = () => {
            if (currentIndex < originalLength) {
                // Typing phase - reveal one character at a time
                const revealed = originalText.substring(0, currentIndex + 1);
                element.innerHTML = revealed + showCursor();
                currentIndex++;
                
                // Variable typing speed for more natural feel
                const delay = Math.random() * 30 + 20;
                scrambleTimersRef.current[element] = setTimeout(updateText, delay);
            } else {
                // Done - show final text without cursor
                element.textContent = originalText;
                if (onComplete) onComplete();
            }
        };
        
        // Start with empty text and cursor
        element.innerHTML = showCursor();
        
        // Start the animation
        scrambleTimersRef.current[element] = setTimeout(updateText, 50);
    };

    const handleTitleHover = (index, originalText) => {
        const titleElement = titleRefs.current[index];
        if (!titleElement) return;
        
        // Clear any existing timer
        if (scrambleTimersRef.current[titleElement]) {
            clearTimeout(scrambleTimersRef.current[titleElement]);
            delete scrambleTimersRef.current[titleElement];
        }
        
        // Start typewriter effect
        typewriterText(titleElement, originalText);
    };

    const handleTitleLeave = (index, originalText) => {
        const titleElement = titleRefs.current[index];
        if (!titleElement) return;
        
        // Clear any existing scramble timer
        if (scrambleTimersRef.current[titleElement]) {
            clearTimeout(scrambleTimersRef.current[titleElement]);
            delete scrambleTimersRef.current[titleElement];
        }
        
        // Reset to original text immediately
        if (titleElement) {
            titleElement.textContent = originalText;
        }
    };

    const handleProjectNavigation = (project) => {
        if (project.externalLink) {
            // Navigate in the same tab to static folder
            window.location.assign(project.externalLink);
        } else {
            const projectSlug = getProjectSlug(project.title);
            const event = new CustomEvent('navigation', { detail: { path: `/work/${projectSlug}` } });
            window.dispatchEvent(event);
        }
    };

    const sorted = [...projects]
        .filter(project => !project.hidden)
        .sort((a, b) => parseInt(b.year) - parseInt(a.year));

    const slideshowItems = sorted.map((project) => ({
        id: project.id,
        src: project.image || '',
        aspect: project.aspect || '16:9',
        title: project.title,
    }));

    const getIsTouchDevice = () => {
        if (typeof window === 'undefined') return false;

        let isCoarse = false;

        try {
            if (window.matchMedia) {
                const coarseMq = window.matchMedia('(pointer: coarse)');
                isCoarse = coarseMq.matches;
            }
        } catch {
            // ignore matchMedia errors and fall back to dimensions
        }

        return isCoarse;
    };

    const handleProjectClick = (project) => {
        // On all devices (including touch/slideshow), a single tap/click opens the project
        handleProjectNavigation(project);
    };

    useEffect(() => {
        if (useSlideshow) return;

        const onMove = (e) => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
            // Reset scale to 70% when mouse moves
            if (hoveredProject) {
                setPreviewScale(0.7);
                // Clear existing timer
                if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current);
                }
                // Start new timer - grow to 100% after 100ms of stillness
                hoverTimerRef.current = setTimeout(() => {
                    setPreviewScale(1.0);
                }, 100);
            }
        };
        document.addEventListener('mousemove', onMove);
        return () => document.removeEventListener('mousemove', onMove);
    }, [hoveredProject, useSlideshow]);

    useEffect(() => {
        const updateMode = () => {
            setUseSlideshow(getIsTouchDevice());
        };

        updateMode();
        window.addEventListener('resize', updateMode);
        window.addEventListener('orientationchange', updateMode);

        return () => {
            window.removeEventListener('resize', updateMode);
            window.removeEventListener('orientationchange', updateMode);
        };
    }, []);

    useEffect(() => {
        if (!useSlideshow || slideshowItems.length === 0) {
            if (slideshowIntervalRef.current) {
                clearInterval(slideshowIntervalRef.current);
                slideshowIntervalRef.current = null;
            }
            return;
        }

        if (slideshowIntervalRef.current) {
            clearInterval(slideshowIntervalRef.current);
        }

        slideshowIntervalRef.current = setInterval(() => {
            setSlideshowIndex(prev => {
                const next = (prev + 1) % slideshowItems.length;
                return next;
            });
        }, 3000);

        return () => {
            if (slideshowIntervalRef.current) {
                clearInterval(slideshowIntervalRef.current);
                slideshowIntervalRef.current = null;
            }
        };
    }, [useSlideshow, slideshowItems.length]);

    // Cleanup scramble timers on unmount
    useEffect(() => {
        return () => {
            Object.values(scrambleTimersRef.current).forEach(timer => {
                if (timer) clearTimeout(timer);
            });
            scrambleTimersRef.current = {};
        };
    }, []);

    // GSAP fade-up animation for projects
    useEffect(() => {
        // Wait for next tick to ensure refs are set
        const timer = setTimeout(() => {
            projectRefs.current.forEach((ref, index) => {
                if (ref) {
                    gsap.fromTo(
                        ref,
                        {
                            opacity: 0,
                            y: 50
                        },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: ref,
                            start: 'top 85%',
                            toggleActions: 'play none none none'
                        },
                        delay: index * 0.05 // Stagger animation - faster
                    }
                    );
                }
            });
        }, 0);

        return () => {
            clearTimeout(timer);
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [sorted.length]); // Re-run when projects change

    // Cleanup hover timer on unmount
    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
            }
            if (slideshowIntervalRef.current) {
                clearInterval(slideshowIntervalRef.current);
            }
        };
    }, []);

    const currentSlide = slideshowItems[slideshowIndex] || null;

    return (
        <>
            <SEO
                title="Portfolio | 3D Art & Interactive Installations | Jacob Faulkner"
                description="Explore the portfolio of Jacob Faulkner, featuring 3D art, interactive installations, VR experiences, and creative technology projects. Based in England, UK."
                keywords="3D artist, creative technologist, interactive installations, digital media, real-time graphics, Unity, TouchDesigner, Cinema 4D, VR, portfolio, Jacob Faulkner, Ordos"
                url="/work"
            />
            <StructuredData data={generateCollectionPageSchema()} />
            <div className="work-page">
            {useSlideshow && currentSlide && (
                <div className="work-hero-slideshow work-hero-slideshow--touch">
                    {(() => {
                        const src = currentSlide.src || '';
                        const isVideo = /\.(mp4|webm|ogg)$/i.test(src);
                        const isImage = /\.(png|jpe?g|webp|gif)$/i.test(src);

                        if (isVideo) {
                            return (
                                <video
                                    key={src}
                                    src={src}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            );
                        }

                        if (isImage) {
                            return <img key={src} src={src} alt={currentSlide.title} />;
                        }

                        return (
                            <div className="work-hero-slideshow-fallback" />
                        );
                    })()}
                    <div className="work-hero-overlay work-hero-overlay--touch" />
                </div>
            )}
            <div className="work-header">
                <h1 className="animate-line">Projects</h1>
                <p className="work-subtitle animate-line">Selected works, wip projects, concepts and ideas.</p>
            </div>

            <div className="projects-list">
                {sorted.map((project, index) => (
                    <button
                        key={project.id}
                        ref={el => projectRefs.current[index] = el}
                        className="project-row"
                        onClick={() => handleProjectClick(project)}
                        onMouseEnter={() => {
                            setHoveredProject(project);
                            setHoverTimestamp(Date.now()); // Force animation restart
                            setPreviewScale(0.7); // Start at 70%
                            // Clear any existing timer
                            if (hoverTimerRef.current) {
                                clearTimeout(hoverTimerRef.current);
                            }
                            // After 100ms of stillness, grow to 100%
                            hoverTimerRef.current = setTimeout(() => {
                                setPreviewScale(1.0);
                            }, 100);
                        }}
                        onMouseLeave={() => {
                            setHoveredProject(null);
                            setPreviewScale(0.7);
                            if (hoverTimerRef.current) {
                                clearTimeout(hoverTimerRef.current);
                            }
                        }}
                    >
                        <div className="project-row-title">
                            <span 
                                className="project-row-name"
                                ref={el => titleRefs.current[index] = el}
                                onMouseEnter={() => handleTitleHover(index, project.title)}
                                onMouseLeave={() => handleTitleLeave(index, project.title)}
                            >
                                {project.title}
                            </span>
                            <span className="project-row-meta">{project.category}</span>
                        </div>
                        <div className="project-row-desc">{project.description}</div>
                    </button>
                ))}
            </div>

            {!useSlideshow && hoveredProject && (
                <div
                    key={hoveredProject.id}
                    className="hover-preview"
                    style={{ 
                        left: cursorPosition.x, 
                        top: cursorPosition.y,
                        transform: `translate(-50%, -50%) scale(${previewScale})`,
                        transition: 'transform 0.1s ease-out'
                    }}
                >
                    <div className="hover-preview-image-wrapper" style={{
                        aspectRatio: (() => {
                            const val = hoveredProject?.aspect || '16:9';
                            const parts = val.replace(/\s/g, '').split(':');
                            if (parts.length === 2) {
                                const w = parseFloat(parts[0]);
                                const h = parseFloat(parts[1]);
                                if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) return `${w} / ${h}`;
                            }
                            return '16 / 9';
                        })()
                    }}>
                        <div className="hover-preview-image-container">
                            {(() => {
                                const src = hoveredProject.image || '';
                                const isVideo = /\.(mp4|webm|ogg)$/i.test(src);
                                const isImage = /\.(png|jpe?g|webp|gif)$/i.test(src);

                                if (isVideo) {
                                    return (
                                        <video
                                            src={src}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                    );
                                }

                                if (isImage) {
                                    return <img src={src} alt={hoveredProject.title} />;
                                }

                                return (
                                    <div className="hover-preview-bg" style={{ background: src }}></div>
                                );
                            })()}
                        </div>
                        <div className="hover-dissolve" key={`dissolve-${hoveredProject.id}-${hoverTimestamp}`}>
                            {Array.from({ length: 96 }).map((_, i) => {
                                // Calculate spiral delay: 12 columns, 8 rows
                                const cols = 12;
                                const rows = 8;
                                const col = i % cols;
                                const row = Math.floor(i / cols);
                                
                                // Center point
                                const centerX = cols / 2 - 0.5;
                                const centerY = rows / 2 - 0.5;
                                
                                // Distance from center
                                const dx = col - centerX;
                                const dy = row - centerY;
                                const distance = Math.sqrt(dx * dx + dy * dy);
                                
                                // Angle from center (in radians)
                                const angle = Math.atan2(dy, dx);
                                
                                // Normalize angle to 0-2π and create spiral effect
                                // Combine distance and angle for spiral pattern
                                const spiralValue = distance * 2 + (angle + Math.PI) / (2 * Math.PI) * 4;
                                
                                return (
                                    <div
                                        key={i}
                                        className="hover-dissolve-cell"
                                        style={{ animationDelay: `${spiralValue * 33}ms` }}
                                    />
                                );
                            })}
                        </div>
                        <div className="hover-dissolve-grid" key={`dissolve-grid-${hoveredProject.id}-${hoverTimestamp}`}>
                            {Array.from({ length: 96 }).map((_, i) => {
                                // Calculate spiral delay: 12 columns, 8 rows
                                const cols = 12;
                                const rows = 8;
                                const col = i % cols;
                                const row = Math.floor(i / cols);
                                
                                // Center point
                                const centerX = cols / 2 - 0.5;
                                const centerY = rows / 2 - 0.5;
                                
                                // Distance from center
                                const dx = col - centerX;
                                const dy = row - centerY;
                                const distance = Math.sqrt(dx * dx + dy * dy);
                                
                                // Angle from center (in radians)
                                const angle = Math.atan2(dy, dx);
                                
                                // Normalize angle to 0-2π and create spiral effect (reversed)
                                // Combine distance and angle for spiral pattern
                                const spiralValue = distance * 2 + (angle + Math.PI) / (2 * Math.PI) * 4;
                                
                                return (
                                    <div
                                        key={i}
                                        className="hover-dissolve-grid-cell"
                                        style={{ animationDelay: `${spiralValue * 33}ms` }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <div className="hover-preview-title">
                        <div className="hover-preview-title-text">
                            {(() => {
                                const title = hoveredProject.title;
                                if (title.includes(' --- ')) {
                                    return title.split(' --- ')[0];
                                } else if (title.includes(' -- ')) {
                                    return title.split(' -- ')[0];
                                } else if (title.includes(' — ')) {
                                    return title.split(' — ')[0];
                                } else if (title.includes(' - ')) {
                                    return title.split(' - ')[0];
                                }
                                return title;
                            })()}
                        </div>
                        <div className="hover-dissolve hover-dissolve--title" key={`dissolve-title-${hoveredProject.id}-${hoverTimestamp}`}>
                            {Array.from({ length: 96 }).map((_, i) => {
                                // Calculate spiral delay: 12 columns, 8 rows
                                const cols = 12;
                                const rows = 8;
                                const col = i % cols;
                                const row = Math.floor(i / cols);
                                
                                // Center point
                                const centerX = cols / 2 - 0.5;
                                const centerY = rows / 2 - 0.5;
                                
                                // Distance from center
                                const dx = col - centerX;
                                const dy = row - centerY;
                                const distance = Math.sqrt(dx * dx + dy * dy);
                                
                                // Angle from center (in radians)
                                const angle = Math.atan2(dy, dx);
                                
                                // Normalize angle to 0-2π and create spiral effect
                                // Combine distance and angle for spiral pattern
                                const spiralValue = distance * 2 + (angle + Math.PI) / (2 * Math.PI) * 4;
                                
                                return (
                                    <div
                                        key={i}
                                        className="hover-dissolve-cell"
                                        style={{ animationDelay: `${500 + (spiralValue * 33)}ms` }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default WorkPage; 